import {
  getPlanningPhaseBySlug,
  getPlanningPhasePortalPath,
  getPlanningPhases,
} from './planningPhases';

export const planningPortalTierKeys = Object.freeze([
  'primary',
  'secondary',
  'additional',
]);

const ITINERARY_GALLERY_RESOURCE = Object.freeze({
  id: 'itinerary-planner',
  renderer: 'featured-itinerary-gallery',
});

const GUIDE_COLLECTIONS_RESOURCE = Object.freeze({
  id: 'guide-collections',
  renderer: 'guide-collection-navigator',
});

const PHASE_ONE_PRIMARY_RESOURCES = Object.freeze([
  Object.freeze({ id: 'island-chooser', renderer: 'island-chooser-preview' }),
]);

const PHASE_ONE_SECONDARY_RESOURCES = Object.freeze([
  Object.freeze({ id: 'region-guide', renderer: 'island-region-overview' }),
  Object.freeze({ id: 'weather-comparison', renderer: 'destination-weather-comparison' }),
  Object.freeze({ id: 'activity-guides', renderer: 'considering-trip-resource', section: 'activity-guides' }),
  Object.freeze({ id: 'planning-reads', renderer: 'considering-trip-resource', section: 'planning-reads' }),
  Object.freeze({ id: 'family-reads', renderer: 'considering-trip-resource', section: 'family-reads' }),
]);

const PHASE_ONE_ADDITIONAL_RESOURCES = Object.freeze([
  Object.freeze({ id: 'island-context', renderer: 'considering-trip-resource', section: 'island-context' }),
  Object.freeze({ id: 'activity-profiles', renderer: 'considering-trip-resource', section: 'activity-profiles' }),
]);

const PHASE_TWO_PRIMARY_RESOURCES = Object.freeze([
  Object.freeze({ id: 'home-base-recommender', renderer: 'home-base-recommender-preview' }),
  Object.freeze({ id: 'region-comparison', renderer: 'region-stay-comparison' }),
  Object.freeze({ id: 'weather-comparison', renderer: 'weather-comparison-preview' }),
]);

const PHASE_TWO_SECONDARY_RESOURCES = Object.freeze([
  Object.freeze({ id: 'stay-research', renderer: 'stay-research-navigator' }),
]);

const PHASE_TWO_SUPPORTING_RESOURCES = Object.freeze([
  Object.freeze({ id: 'nearby-research', renderer: 'nearby-research-navigator' }),
  Object.freeze({ id: 'stay-planning-reads', renderer: 'stay-planning-reads' }),
]);

const PHASE_THREE_SUPPORTING_RESOURCES = Object.freeze([
  Object.freeze({ id: 'light-planning', renderer: 'light-planning-preview' }),
  Object.freeze({ id: 'home-base-guides', renderer: 'home-base-explorer' }),
  Object.freeze({ id: 'planning-reads', renderer: 'curated-planning-reads' }),
]);

const PHASE_FOUR_PRIMARY_RESOURCES = Object.freeze([
  Object.freeze({
    id: 'itinerary-planner',
    renderer: 'featured-itinerary-gallery',
    eyebrow: 'Recommended first',
    heading: 'Finish the itinerary before you pack.',
    description: 'With departure close, reservations, drive times, meals, and backup plans need to work together. A custom itinerary closes those gaps around your dates, lodging, group, and priorities.',
    analyticsSource: 'planning_phase_final_details',
  }),
]);

const PHASE_FOUR_SECONDARY_RESOURCES = Object.freeze([
  Object.freeze({
    id: 'final-reservations-guides',
    renderer: 'guide-collection-navigator',
    collectionIds: Object.freeze(['where-to-eat', 'things-to-do', 'hikes']),
    eyebrow: 'Confirm the anchors',
    heading: 'Reserve what cannot stay flexible.',
    description: 'Use restaurant, activity, and hiking guides to verify booking windows, hours, timing, and practical details before departure.',
    analyticsSource: 'planning_phase_final_details_guides',
  }),
]);

const PHASE_FOUR_SUPPORTING_RESOURCES = Object.freeze([
  Object.freeze({ id: 'beach-conditions', renderer: 'beach-conditions-preparation' }),
  Object.freeze({
    id: 'sunrise-sunset',
    renderer: 'light-planning-preview',
    eyebrow: 'Check the clock',
    heading: 'Put daylight into the final plan.',
    description: 'Sunrise, sunset, and terrain shade change by date and place. Check the actual timing before fixing early starts, dinners, drives, and photo stops.',
    analyticsSource: 'planning_phase_final_details_sun',
  }),
  Object.freeze({ id: 'prep-reads', renderer: 'final-details-resource', section: 'prep-reads' }),
  Object.freeze({ id: 'travel-well', renderer: 'final-details-resource', section: 'travel-well' }),
  Object.freeze({ id: 'reservation-profiles', renderer: 'final-details-resource', section: 'reservation-profiles' }),
]);

const PHASE_FIVE_PRIMARY_RESOURCES = Object.freeze([
  Object.freeze({
    id: 'beach-conditions',
    renderer: 'beach-conditions-preparation',
    eyebrow: 'Start with what is happening now',
    heading: 'Find the right beach for today.',
    description: 'Water, wind, surf, and rain vary across the island and throughout the day. Compare current conditions by activity before choosing where to go.',
    tier: 'primary',
    compact: false,
  }),
]);

const PHASE_FIVE_SECONDARY_RESOURCES = Object.freeze([
  Object.freeze({
    id: 'photo-planner',
    renderer: 'light-planning-preview',
    requiredCapability: 'photoPlanner',
    eyebrow: 'Find the view',
    heading: 'Put the camera in the right place.',
    description: 'Choose a scene that fits where you are headed, then open the Photo Planner for location-specific composition and timing guidance.',
    analyticsSource: 'planning_phase_arrived_photo',
    mode: 'photo',
    spotSet: 'photo',
  }),
  Object.freeze({
    id: 'sunrise-sunset',
    renderer: 'light-planning-preview',
    eyebrow: 'Use the remaining light',
    heading: 'Know when the day changes.',
    description: 'Check sunrise, sunset, and terrain shade for a specific place before timing a drive, dinner, beach stop, or lookout.',
    analyticsSource: 'planning_phase_arrived_sun',
    mode: 'sun',
  }),
  Object.freeze({ id: 'dining-guides', renderer: 'on-island-resource', section: 'dining-guides' }),
]);

const PHASE_FIVE_SUPPORTING_RESOURCES = Object.freeze([
  Object.freeze({ id: 'flexible-guides', renderer: 'on-island-resource', section: 'flexible-guides' }),
  Object.freeze({ id: 'day-of-reads', renderer: 'on-island-resource', section: 'day-of-reads' }),
  Object.freeze({ id: 'local-profiles', renderer: 'on-island-resource', section: 'local-profiles' }),
]);

const createResourceTiers = (phaseId) => Object.freeze({
  primary: Object.freeze(
    phaseId === 'considering-trip'
      ? PHASE_ONE_PRIMARY_RESOURCES
      : phaseId === 'where-to-stay'
      ? PHASE_TWO_PRIMARY_RESOURCES
      : phaseId === 'what-to-do'
        ? [ITINERARY_GALLERY_RESOURCE]
        : phaseId === 'final-details'
          ? PHASE_FOUR_PRIMARY_RESOURCES
          : phaseId === 'arrived'
            ? PHASE_FIVE_PRIMARY_RESOURCES
        : [],
  ),
  secondary: Object.freeze(
    phaseId === 'considering-trip'
      ? PHASE_ONE_SECONDARY_RESOURCES
      : phaseId === 'where-to-stay'
      ? PHASE_TWO_SECONDARY_RESOURCES
      : phaseId === 'what-to-do'
        ? [GUIDE_COLLECTIONS_RESOURCE]
        : phaseId === 'final-details'
          ? PHASE_FOUR_SECONDARY_RESOURCES
          : phaseId === 'arrived'
            ? PHASE_FIVE_SECONDARY_RESOURCES
        : [],
  ),
  additional: Object.freeze(
    phaseId === 'considering-trip'
      ? PHASE_ONE_ADDITIONAL_RESOURCES
      : phaseId === 'where-to-stay'
      ? PHASE_TWO_SUPPORTING_RESOURCES
      : phaseId === 'what-to-do'
        ? PHASE_THREE_SUPPORTING_RESOURCES
        : phaseId === 'final-details'
          ? PHASE_FOUR_SUPPORTING_RESOURCES
          : phaseId === 'arrived'
            ? PHASE_FIVE_SUPPORTING_RESOURCES
        : [],
  ),
});

export function getPlanningPhasePortalConfig(phaseSlug, islandName) {
  const phase = getPlanningPhaseBySlug(phaseSlug, islandName);
  if (!phase) return null;

  return Object.freeze({
    ...phase,
    path: getPlanningPhasePortalPath(phase.id),
    resourceTiers: createResourceTiers(phase.id),
  });
}

export function getPlanningPhasePortalConfigs(islandName) {
  return getPlanningPhases(islandName).map((phase) => (
    getPlanningPhasePortalConfig(phase.slug, islandName)
  ));
}

export function filterPlanningResourceTiers(resourceTiers, capabilities = {}) {
  return Object.freeze(Object.fromEntries(
    planningPortalTierKeys.map((tierKey) => [
      tierKey,
      Object.freeze((resourceTiers?.[tierKey] || []).filter((resource) => (
        !resource.requiredCapability || capabilities[resource.requiredCapability] !== false
      ))),
    ]),
  ));
}

export function getPlanningPortalNavigationPath(path, islandKey) {
  if (typeof window === 'undefined') return path;

  const isLocalPreview = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const hasIslandPreview = new URLSearchParams(window.location.search).has('island');
  if (!isLocalPreview || !hasIslandPreview) return path;

  const params = new URLSearchParams({ island: islandKey });
  return `${path}?${params.toString()}`;
}
